<template>
  <div class="landing">
    <!-- Skip link (WCAG 2.4.1) -->
    <a href="#contenido" class="skip-link">Saltar al contenido</a>

    <!-- HEADER — sticky (patrón de /para/[vertical], que no tapa el titular) -->
    <header class="landing-header">
      <div class="header-content">
        <NuxtLink to="/" class="logo">Comanda<span>Plus</span></NuxtLink>

        <nav class="landing-nav" aria-label="Secciones de la página">
          <a v-for="link in navLinks" :key="link.href" :href="link.href">{{ link.label }}</a>
        </nav>

        <a href="/auth?modo=registro" class="btn-primary header-cta">Empezar gratis</a>

        <button
          class="nav-toggle"
          type="button"
          :aria-expanded="menuAbierto"
          aria-controls="menu-movil"
          :aria-label="menuAbierto ? 'Cerrar menú' : 'Abrir menú'"
          @click="menuAbierto = !menuAbierto"
        >
          <span class="material-symbols-rounded">{{ menuAbierto ? 'close' : 'menu' }}</span>
        </button>
      </div>

      <!-- Menú móvil: el menú no se borra, se pliega -->
      <nav v-show="menuAbierto" id="menu-movil" class="nav-movil" aria-label="Menú">
        <a v-for="link in navLinks" :key="link.href" :href="link.href" @click="menuAbierto = false">
          {{ link.label }}
        </a>
        <a href="/auth?modo=registro" class="btn-primary nav-movil-cta">Empezar gratis · 14 días</a>
      </nav>
    </header>

    <main id="contenido">
      <!-- HERO -->
      <section class="hero-band">
        <div class="hero">
          <div class="hero-text">
            <h1>Tu caja cuadra el lunes. Tus camareros nuevos sirven la primera tarde. Y la mesa de 8 paga en 30 segundos.</h1>
            <p class="hero-what">El sistema con el que tomas comandas, las mandas a cocina y cobras — desde el móvil que ya tienes.</p>
            <p class="subheadline">ComandaPlus es lo que pasa cuando un sistema para hostelería empieza por escuchar al dueño, no por listar funciones. Funciona en el móvil que ya tienes. Sin hardware. Sin permanencia. Listo en una tarde.</p>
            <div class="hero-buttons">
              <a href="/auth?modo=registro" class="btn-primary">Empezar gratis · 14 días</a>
              <a href="#calculadora" class="btn-secondary btn-secondary--dark">Calcular lo que te ahorro</a>
            </div>
            <p v-if="founderWhatsappUrl" class="hero-whatsapp">
              ¿Prefieres que te lo enseñe en tu local?
              <a :href="founderWhatsappUrl" target="_blank" rel="noopener noreferrer">Escríbeme por WhatsApp</a>
            </p>
            <div class="hero-trust">
              <span>Listo en <strong>una tarde</strong></span>
              <span><strong>Cero</strong> hardware</span>
              <span><strong>Cancelas cuando quieras</strong></span>
            </div>
          </div>
          <div class="hero-qr">
            <figure class="hero-product-mockup">
              <video
                ref="heroVideo"
                class="hero-video"
                src="/hero-mockup.webm"
                poster="/hero-mockup-poster.png"
                muted
                loop
                playsinline
                preload="none"
                aria-label="El cliente escanea el QR, elige platos y la cocina los recibe al instante"
              >
                Tu navegador no puede reproducir el vídeo. ComandaPlus permite al cliente pedir desde su móvil con un QR, y la cocina recibe el pedido al instante.
              </video>
              <button class="video-toggle" type="button" @click="toggleVideo('hero')">
                <span class="material-symbols-rounded">{{ heroPlaying ? 'pause' : 'play_arrow' }}</span>
                {{ heroPlaying ? 'Pausar vídeo' : 'Reproducir vídeo' }}
              </button>
            </figure>
          </div>
        </div>
      </section>

      <!-- PUERTA A — La caja del lunes (el mejor argumento va primero) -->
      <section class="usage usage--dark" id="caja">
        <div class="usage-inner">
          <div class="usage-head">
            <div class="section-label">La caja del lunes</div>
            <p class="usage-pain">Un bar que pierde 10 € al día en descuadres tira 3.000 € al año sin enterarse.</p>
            <h2 class="usage-title">El lunes a las 9, abres la pantalla y la caja cuadra.</h2>
            <figure class="quote">
              <blockquote>Buenas chicos, hoy nuevamente le falta dinero a la caja, no puede ser que en menos de dos semanas ya hagan falta casi 60 euros.</blockquote>
              <figcaption>Mensaje real de un dueño a sus camareros, documentado en prensa española</figcaption>
            </figure>
          </div>
          <ol class="usage-stack">
            <li class="usage-stack-step">
              <div class="usage-step-text usage-stack-head"><span class="usage-step-num">1</span><h3>Ves el turno en vivo</h3><p>Ventas, pedidos, mesas y cocina, actualizándose solos. Sin estar encima.</p></div>
              <img class="usage-shot-wide" src="/dueno-1-turno.webp" width="1280" height="837" alt="Resumen del turno en ComandaPlus: ventas del día, pedidos activos, ocupación de mesas y actividad de cocina en vivo" loading="lazy" />
            </li>
            <li class="usage-stack-step">
              <div class="usage-step-text usage-stack-head"><span class="usage-step-num">2</span><h3>La caja cuadra por método de pago</h3><p>Cada cobro fichado al instante: efectivo, tarjeta, Bizum. Con su desglose fiscal.</p></div>
              <img class="usage-shot-wide" src="/dueno-2-caja.webp" width="1280" height="837" alt="Vista de Caja: desglose por método de pago, desglose fiscal con IVA y facturas emitidas del turno" loading="lazy" />
            </li>
            <li class="usage-stack-step">
              <div class="usage-step-text usage-stack-head"><span class="usage-step-num">3</span><h3>Cierras y cuadra. Y si no cuadra, sabes dónde mirar.</h3><p>El cierre Z aparece solo, con el descuadre a la vista y el desglose por camarero, turno y método de pago.</p></div>
              <img class="usage-shot-wide" src="/dueno-3-cierre.webp" width="1280" height="837" alt="Cierre de caja Z: ventas, efectivo real y descuadre 0,00 €, con botones para descargar el Ticket Z y enviarlo al gestor" loading="lazy" />
            </li>
          </ol>
          <div class="usage-cta"><a href="/auth?modo=registro" class="btn-primary">Probarlo en mi local</a></div>
        </div>
      </section>

      <!-- PUERTA C — El camarero nuevo (ya no es una disculpa: es una puerta) -->
      <section class="usage" id="camarero">
        <div class="usage-head">
          <div class="section-label">El camarero nuevo</div>
          <p class="usage-pain">100.000 vacantes sin cubrir el verano pasado. Cada camarero nuevo que entra te cuesta una semana de formación… si aguanta.</p>
          <h2 class="usage-title">El camarero nuevo coge el móvil y sirve la misma tarde. Sin formación.</h2>
          <figure class="quote">
            <blockquote>Es que no te aguantan un turno de 10 horas.</blockquote>
            <figcaption>Hostelero entrevistado por El Español, julio 2025</figcaption>
          </figure>
          <p class="usage-note">Es tan fácil como WhatsApp: tocas el plato, tocas la mesa, ya está. Y si tu local prefiere que pida el cliente con el QR, también — tú eliges, no el sistema.</p>
        </div>
        <ol class="usage-steps usage-steps--three">
          <li class="usage-step">
            <img class="usage-shot-img" src="/camarero-1-tomar-pedido.webp" width="640" height="1309" alt="Pantalla del camarero para tomar el pedido de la mesa 2: platos por categoría con buscador y botón de añadir" loading="lazy" />
            <div class="usage-step-text"><span class="usage-step-num">1</span><h3>Toma nota en la mesa</h3><p>Elige los platos por categoría y los asigna a cada comensal. Con buscador.</p></div>
          </li>
          <li class="usage-step">
            <img class="usage-shot-img" src="/camarero-2-enviar-cocina.webp" width="640" height="1309" alt="Comanda del camarero con notas por plato y el reparto de lo que debe cada comensal, lista para enviar a cocina" loading="lazy" />
            <div class="usage-step-text"><span class="usage-step-num">2</span><h3>Envía a cocina</h3><p>Repasa la comanda, añade notas ("sin cebolla") y la cocina la recibe al instante.</p></div>
          </li>
          <li class="usage-step">
            <img class="usage-shot-img" src="/camarero-3-cobrar.webp" width="640" height="1309" alt="Pantalla de cobro de la mesa: pago único, partes iguales o cada uno lo suyo, con efectivo, tarjeta o Bizum" loading="lazy" />
            <div class="usage-step-text"><span class="usage-step-num">3</span><h3>Cobra la mesa</h3><p>Pago único, a partes iguales o cada uno lo suyo. Efectivo, tarjeta o Bizum.</p></div>
          </li>
        </ol>
        <div class="usage-cta"><a href="/auth?modo=registro" class="btn-primary">Probarlo en mi local</a></div>
      </section>

      <!-- PUERTA B — Una mesa, una cuenta -->
      <section class="usage" id="cuenta-dividida">
        <div class="usage-head">
          <div class="section-label">Una mesa, una cuenta</div>
          <p class="usage-pain">Cada vez más bares cuelgan el cartel "no dividimos cuentas". No por mala voluntad: es que no pueden.</p>
          <h2 class="usage-title">La mesa de 8 paga en 30 segundos. Cada uno desde su móvil.</h2>
          <figure class="quote">
            <blockquote>El establecimiento siempre pierde tres, cuatro o cinco cervezas en la confusión de separar comandas y pagos.</blockquote>
            <figcaption>Camarero entrevistado por El Español, julio 2025</figcaption>
          </figure>
        </div>
        <ol class="usage-steps usage-steps--mobile">
          <li class="usage-step">
            <img class="usage-shot-img" src="/comensal-1-carta.webp" width="640" height="1309" alt="Carta en el móvil del comensal: croquetas caseras de jamón con foto, precio y la etiqueta 'el que más piden'" loading="lazy" />
            <div class="usage-step-text"><span class="usage-step-num">1</span><h3>Escanea y ve la carta</h3><p>El QR de su mesa. Sin descargar nada. Fotos, precios y el plato más pedido.</p></div>
          </li>
          <li class="usage-step">
            <img class="usage-shot-img" src="/comensal-2-pedido.webp" width="640" height="1309" alt="Pedido compartido de la mesa: lo que pidió cada comensal y cuánto le toca a cada uno" loading="lazy" />
            <div class="usage-step-text"><span class="usage-step-num">2</span><h3>Pide, cada uno lo suyo</h3><p>Ve al instante quién pidió qué y cuánto le toca a cada uno.</p></div>
          </li>
          <li class="usage-step">
            <img class="usage-shot-img" src="/comensal-3-dividir.webp" width="640" height="1309" alt="Pantalla de cuenta con el total de la mesa y el botón 'Dividir la cuenta'" loading="lazy" />
            <div class="usage-step-text"><span class="usage-step-num">3</span><h3>Divide la cuenta</h3><p>Un toque en "Dividir la cuenta". Sin que el camarero toque la calculadora.</p></div>
          </li>
          <li class="usage-step">
            <img class="usage-shot-img" src="/comensal-4-pago.webp" width="640" height="1309" alt="Su parte de 16,40 € y las formas de pago: tarjeta, efectivo o Bizum" loading="lazy" />
            <div class="usage-step-text"><span class="usage-step-num">4</span><h3>Cada uno paga su parte</h3><p>Desde su móvil: tarjeta, efectivo o Bizum. Cuando paga el último, la mesa se libera.</p></div>
          </li>
        </ol>
        <div class="usage-cta"><a href="/auth?modo=registro" class="btn-primary">Probarlo en mi local</a></div>
      </section>

      <!-- LO MONTAS TÚ -->
      <section class="setup" id="como-funciona">
        <div class="section-label">Cómo se monta</div>
        <h2 class="setup-title">De cero a recibir pedidos en una tarde. Sin técnicos.</h2>
        <div class="steps">
          <div v-for="step in steps" :key="step.number" class="step">
            <div class="step-number">{{ step.number }}</div>
            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
          </div>
        </div>
        <div class="setup-shots">
          <figure>
            <img src="/salon-vista-mesas.webp" width="2000" height="1317" alt="Vista de Salón de ComandaPlus: mapa de mesas con su aforo y botón para añadir mesa" loading="lazy" />
            <figcaption>Tu salón: añade una mesa y se genera su QR.</figcaption>
          </figure>
          <figure>
            <img src="/carta-gestion.webp" width="2000" height="1317" alt="Gestión de la carta: platos con foto, precio y categoría, listos para editar" loading="lazy" />
            <figcaption>Tu carta: foto, precio y categoría en segundos.</figcaption>
          </figure>
        </div>
      </section>

      <!-- Y ADEMÁS — analíticas (después de las 3 puertas, nunca antes) -->
      <section class="usage usage--compact" id="analiticas">
        <div class="usage-head">
          <div class="section-label">Y además</div>
          <p class="usage-pain">Compras género que acaba en la basura. Y cargas con platos en la carta que casi nadie pide.</p>
          <h2 class="usage-title">Sabes qué se vende y qué no. Y dejas de perder dinero adivinando.</h2>
          <p class="usage-note">El panel te enseña qué platos tiran del negocio y cuáles solo ocupan sitio. Compras lo que de verdad sale y quitas lo que nadie pide — con datos de tu propio local, no a ojo.</p>
        </div>
        <figure class="usage-video">
          <video
            ref="analyticsVideo"
            class="usage-shot-wide"
            poster="/analytics-panel-poster.webp"
            muted
            loop
            playsinline
            preload="none"
            aria-label="Recorrido por el panel de analíticas de ComandaPlus: ventas, productos más vendidos, diagnóstico de carta y porcentaje de self-service"
          >
            <source src="/analytics-panel.webm" type="video/webm" />
            <source src="/analytics-panel.mp4" type="video/mp4" />
          </video>
          <button class="video-toggle video-toggle--light" type="button" @click="toggleVideo('analytics')">
            <span class="material-symbols-rounded">{{ analyticsPlaying ? 'pause' : 'play_arrow' }}</span>
            {{ analyticsPlaying ? 'Pausar vídeo' : 'Reproducir vídeo' }}
          </button>
        </figure>
      </section>

      <!-- CALCULADORA — framing de pérdida, sin ahorros fantasma -->
      <section class="calc" id="calculadora">
        <div class="calc-inner scroll-fade">
          <div class="section-label">Calculadora</div>
          <h2 class="section-title">Calcula lo que estás perdiendo hoy.</h2>
          <p class="section-subtitle">Mete tus números. Solo contamos lo que ComandaPlus resuelve hoy: mermas, descuadres y horas de camarero en cuentas divididas.</p>

          <div class="calc-grid">
            <div class="calc-inputs">
              <label class="calc-input">
                <span class="calc-label">Mesas activas</span>
                <input type="number" inputmode="numeric" v-model.number="calc.mesas" min="1" max="200" />
                <span class="calc-hint">{{ inputs.mesas }} mesas</span>
              </label>
              <label class="calc-input">
                <span class="calc-label">Comensales por día</span>
                <input type="number" inputmode="numeric" v-model.number="calc.comensalesDia" min="1" max="2000" />
                <span class="calc-hint">{{ inputs.comensalesDia }} cubiertos al día</span>
              </label>
              <label class="calc-input">
                <span class="calc-label">Ticket medio (€)</span>
                <input type="number" inputmode="decimal" v-model.number="calc.ticketMedio" min="1" max="500" step="0.5" />
                <span class="calc-hint">{{ inputs.ticketMedio.toFixed(2).replace('.', ',') }} € por comensal</span>
              </label>
              <label class="calc-input">
                <span class="calc-label">% mermas sobre compras</span>
                <input type="number" inputmode="decimal" v-model.number="calc.mermasPct" min="0" max="30" step="0.5" />
                <span class="calc-hint">{{ inputs.mermasPct }} % (media sectorial: 10–15 %)</span>
              </label>
            </div>

            <div class="calc-output">
              <div class="calc-output-row">
                <span>Mermas evitables</span>
                <strong class="calc-num">{{ formatEuro(savings.mermas) }}/año</strong>
              </div>
              <div class="calc-output-row">
                <span>Descuadres de caja</span>
                <strong class="calc-num">{{ formatEuro(savings.descuadres) }}/año</strong>
              </div>
              <div class="calc-output-row">
                <span>Horas de camarero en cuentas divididas</span>
                <strong class="calc-num">{{ formatEuro(savings.tiempo) }}/año</strong>
              </div>
              <div class="calc-output-row calc-output-total">
                <span>Lo que pierdes hoy</span>
                <strong class="calc-num calc-num-big">{{ formatEuro(savings.total) }}/año</strong>
              </div>
              <div class="calc-output-cost">
                <span>Coste ComandaPlus Pro (49 €/mes)</span>
                <strong class="calc-num">{{ formatEuro(COSTE_ANUAL_PRO) }}/año</strong>
              </div>
              <div class="calc-output-ratio">
                ComandaPlus se paga <strong>{{ savings.ratio }}× sobre lo que pierdes hoy</strong>
              </div>
              <a href="/auth?modo=registro" class="btn-primary calc-cta">Empezar gratis · 14 días</a>
              <p class="calc-note">Datos de Loomis Pay y Ninja TPV. Estimación conservadora.</p>
            </div>
          </div>
        </div>
      </section>

      <!-- LO QUE NO TE PEDIMOS -->
      <section class="why-us" id="por-que">
        <div class="section-label scroll-fade">Lo que no te pedimos</div>
        <h2 class="section-title scroll-fade">Cero hardware. Cero permanencia. Cero esperas.</h2>
        <p class="section-subtitle scroll-fade">Si te lo piensas dos veces antes de cambiar de sistema, es por estas seis cosas.</p>
        <div class="why-grid">
          <div v-for="reason in reasons" :key="reason.title" class="why-card scroll-fade">
            <span class="material-symbols-rounded why-icon">{{ reason.icon }}</span>
            <h3>{{ reason.title }}</h3>
            <p>{{ reason.description }}</p>
          </div>
        </div>
      </section>

      <!-- PRECIOS — 29 / 49 / 89 (fuente: docs/MENSAJES-MAESTROS.md §5) -->
      <section class="pricing" id="precios">
        <div class="section-label scroll-fade">Precios</div>
        <h2 class="section-title scroll-fade">Sin hardware. Sin permanencia. Sin coste de instalación.</h2>
        <p class="section-subtitle scroll-fade">Pagas el mes y te vas cuando quieras. Tu precio queda congelado 3 años desde que entras.</p>

        <div class="pricing-grid">
          <div v-for="plan in planes" :key="plan.name" class="plan scroll-fade" :class="{ featured: plan.featured }">
            <div v-if="plan.featured" class="plan-badge">El más elegido</div>
            <div class="plan-name">{{ plan.name }}</div>
            <div class="plan-limit">{{ plan.para }}</div>
            <div class="plan-price">{{ plan.price }}<span>&euro;</span></div>
            <div class="plan-period">{{ plan.period }}</div>
            <ul class="plan-features">
              <li v-for="f in plan.features" :key="f">{{ f }}</li>
            </ul>
            <a href="/auth?modo=registro" class="btn-primary">Empezar gratis · 14 días</a>
          </div>
        </div>

        <div class="verifactu-notice scroll-fade">
          <span class="material-symbols-rounded verifactu-icon">build</span>
          <span><strong>VeriFactu: en desarrollo.</strong> Estará listo antes de que entre en vigor (enero 2027 sociedades, julio 2027 autónomos). Se incluye sin coste adicional.</span>
        </div>
      </section>

      <!-- FAQ — las 7 canónicas (docs/MENSAJES-MAESTROS.md §10) -->
      <section class="faq" id="preguntas">
        <div class="section-label scroll-fade">Preguntas frecuentes</div>
        <h2 class="section-title scroll-fade">Lo que nos suelen preguntar</h2>
        <div class="faq-list scroll-fade">
          <div v-for="(item, i) in faqItems" :key="i" class="faq-item" :class="{ open: item.open }">
            <button
              class="faq-question"
              type="button"
              :aria-expanded="item.open"
              :aria-controls="`faq-answer-${i}`"
              @click="item.open = !item.open"
            >
              <span>{{ item.question }}</span>
              <span class="faq-toggle" aria-hidden="true">+</span>
            </button>
            <div :id="`faq-answer-${i}`" class="faq-answer">
              <p>{{ item.answer }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- CTA FINAL — camino, no culpa -->
      <section class="cta-final">
        <h2>El lunes que viene tu caja puede cuadrar sola.</h2>
        <p>Lo montas en una tarde: subes tu carta, imprimes los QR y esa noche ya recibes pedidos. Sin hardware, sin permanencia, sin técnico.</p>
        <a href="/auth?modo=registro" class="btn-primary">Empezar gratis · 14 días</a>
      </section>
    </main>

    <!-- FOOTER — mapa del sitio, no solo línea legal -->
    <footer class="landing-footer">
      <div class="footer-cols">
        <div class="footer-col">
          <h2 class="footer-title">Producto</h2>
          <a href="#caja">La caja del lunes</a>
          <a href="#cuenta-dividida">Una mesa, una cuenta</a>
          <a href="#camarero">El camarero nuevo</a>
          <a href="#precios">Precios</a>
        </div>
        <div class="footer-col">
          <h2 class="footer-title">Para tu local</h2>
          <NuxtLink to="/para/bares">Bares y tabernas</NuxtLink>
          <NuxtLink to="/para/restaurantes">Restaurantes</NuxtLink>
          <NuxtLink to="/para/cafeterias">Cafeterías</NuxtLink>
        </div>
        <div class="footer-col">
          <h2 class="footer-title">Legal</h2>
          <NuxtLink to="/legal/terminos">Términos</NuxtLink>
          <NuxtLink to="/legal/privacidad">Privacidad</NuxtLink>
          <NuxtLink to="/legal/cookies">Cookies</NuxtLink>
        </div>
      </div>
      <p class="footer-copy">ComandaPlus &copy; 2026 &mdash; Dieter Jos&eacute; Lorenzo Molina</p>
    </footer>

    <!-- BARRA INFERIOR MÓVIL — el CTA siempre a un pulgar de distancia (Ley de Fitts) -->
    <div class="cta-bar">
      <a href="#precios" class="cta-bar-link">Precios</a>
      <a href="/auth?modo=registro" class="btn-primary cta-bar-btn">Empezar gratis</a>
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({ layout: false })

// ─── Navegación ────────────────────────────────────────────────────
// Etiqueta = destino. Las 3 puertas comerciales tienen puerta en el menú.
const navLinks = [
  { label: 'La caja', href: '#caja' },
  { label: 'Camarero nuevo', href: '#camarero' },
  { label: 'Cuenta dividida', href: '#cuenta-dividida' },
  { label: 'Precios', href: '#precios' },
  { label: 'Preguntas', href: '#preguntas' },
]
const menuAbierto = ref(false)

// ─── WhatsApp del founder (CTA secundario, nunca el principal) ──────
// Si NUXT_PUBLIC_FOUNDER_WHATSAPP está vacío, el enlace NO se pinta.
// Antes caía a /auth manteniendo el texto "Te lo enseño en tu local":
// el botón mentía sobre su destino. Un CTA nunca cambia de destino sin
// cambiar de texto.
const config = useRuntimeConfig()
const founderWhatsappUrl = computed(() => {
  const num = String(config.public.founderWhatsapp ?? '').trim()
  if (!num) return ''
  const msg = 'Hola Dieter, quiero ver ComandaPlus en mi local.'
  return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`
})

// ─── Vídeos: control de pausa (WCAG 2.2.2, Nivel A) + carga diferida ──
const heroVideo = ref<HTMLVideoElement | null>(null)
const analyticsVideo = ref<HTMLVideoElement | null>(null)
const heroPlaying = ref(false)
const analyticsPlaying = ref(false)

const toggleVideo = (cual: 'hero' | 'analytics') => {
  const el = cual === 'hero' ? heroVideo.value : analyticsVideo.value
  if (!el) return
  const flag = cual === 'hero' ? heroPlaying : analyticsPlaying
  if (el.paused) {
    el.play().then(() => { flag.value = true }).catch(() => { flag.value = false })
  } else {
    el.pause()
    flag.value = false
  }
}

// ─── Calculadora ───────────────────────────────────────────────────
// Solo se cuenta lo que el producto resuelve HOY.
// La línea "multas de registro horario evitadas — 1.000 €/año" se eliminó
// (auditoría LexiCore 2026-07-15, hallazgo grave 3): ComandaPlus NO tiene
// fichaje horario, así que era cobrar por un ahorro inexistente.
// Cifras del banco autorizado (docs/MENSAJES-MAESTROS.md §13):
//   - Mermas: 10-15 % de las compras (Loomis Pay). Ahorro alcanzable = la mitad.
//   - Descuadres: hasta 3.000 €/año por bar mediano (Ninja TPV), escalado por mesas.
//   - Horas de camarero en cuentas divididas: 4 h/sem × 52 × 10 €/h = 2.080 €.
const COSTE_ANUAL_PRO = 588 // 49 €/mes × 12 (plan Pro, MENSAJES-MAESTROS §5)

const calc = reactive({
  mesas: 12,
  comensalesDia: 80,
  ticketMedio: 18,
  mermasPct: 12,
})

// Saneado: un campo vacío o a 0 producía NaN y "se paga NaN×".
const limita = (v: unknown, min: number, max: number, porDefecto: number) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return porDefecto
  return Math.min(max, Math.max(min, n))
}

const inputs = computed(() => ({
  mesas: limita(calc.mesas, 1, 200, 12),
  comensalesDia: limita(calc.comensalesDia, 1, 2000, 80),
  ticketMedio: limita(calc.ticketMedio, 1, 500, 18),
  mermasPct: limita(calc.mermasPct, 0, 30, 12),
}))

const savings = computed(() => {
  const i = inputs.value
  const ventasAnuales = i.comensalesDia * i.ticketMedio * 365
  const compras = ventasAnuales * 0.30
  const mermas = Math.max(0, (compras * i.mermasPct / 100) / 2)
  const descuadres = (i.mesas / 12) * 3000
  const tiempo = 2080

  const total = Math.round(mermas + descuadres + tiempo)
  const ratio = Math.max(1, Math.round(total / COSTE_ANUAL_PRO))

  return {
    mermas: Math.round(mermas),
    descuadres: Math.round(descuadres),
    tiempo,
    total,
    ratio,
  }
})

const formatEuro = (n: number) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)

// ─── Contenido ─────────────────────────────────────────────────────
const steps = [
  { number: 1, title: 'Sube tu carta', description: 'Regístrate, crea tu local y añade tus platos con precios. Todo desde el navegador de tu móvil.' },
  { number: 2, title: 'Pon los QR en las mesas', description: 'ComandaPlus genera un QR para cada mesa. Imprímelo o ponlo en un soporte. Es el único "hardware" que necesitas.' },
  { number: 3, title: 'Tus clientes piden, tú controlas', description: 'El cliente escanea y pide. Cocina lo recibe al instante. Tú lo ves todo desde tu panel.' },
]

// Precios canónicos: docs/MENSAJES-MAESTROS.md §5.
const planes = [
  {
    name: 'Esencial',
    para: 'Bares pequeños · 1 local · menos de 10 mesas',
    price: '29',
    period: 'al mes + IVA',
    featured: false,
    features: [
      'Cierre de caja con desglose por camarero y turno',
      'Comandas directas a cocina',
      'Carta digital con QR',
      'Modo barra',
      'Exportación contable',
      'Soporte directo por WhatsApp',
    ],
  },
  {
    name: 'Pro',
    para: 'Restaurantes y pubs · 1 local',
    price: '49',
    period: 'al mes + IVA',
    featured: true,
    features: [
      'Todo lo del plan Esencial',
      'Cuenta dividida desde el móvil del comensal',
      'Pedidos QR desde la mesa',
      'Gestión de salón y mesas ilimitadas',
      'Analíticas y diagnóstico de carta',
      'Gestión de equipo con permisos por rol',
      'VeriFactu incluido cuando entre en vigor',
    ],
  },
  {
    name: 'Multi',
    para: 'Cadenas de 2 o más locales',
    price: '89',
    period: 'al mes + IVA, por local · 15 % dto. desde el 2º',
    featured: false,
    features: [
      'Todo lo del plan Pro, en cada local',
      'Panel agregado de todos tus locales',
      'Comparativa de ventas entre locales',
      'Equipo y permisos por local',
    ],
  },
]

const reasons = [
  { icon: 'block', title: 'No compras nada', description: 'Aquí no compras tablet, ni impresora, ni datáfono. Funciona en el móvil que ya llevas en el bolsillo.' },
  { icon: 'schedule', title: 'No esperas a un técnico', description: 'Los otros sistemas te bloquean medio día con un instalador. Tú entras, subes tu carta, imprimes los QR y esa misma tarde ya recibes pedidos. Solo, desde tu casa.' },
  { icon: 'flag', title: 'No traduces software inglés', description: 'Factura simplificada, propinas separadas del IVA, CSV listo para tu gestoría. Hecho aquí, para Hacienda de aquí.' },
  { icon: 'lock_open', title: 'No firmas permanencia', description: 'Pagas el mes y te vas cuando quieras. Sin penalizaciones, sin "es que tu contrato dura 24 meses". Si lo decides, cancelas en 60 segundos desde tu panel.' },
  { icon: 'handshake', title: 'No hablas con un bot', description: 'WhatsApp directo. Te contesta una persona que ha trabajado en una sala. Sin tickets, sin colas.' },
  { icon: 'receipt_long', title: 'No te pilla 2027 fuera de juego', description: 'Estamos construyendo VeriFactu para que esté antes del plazo. Cuando se active, va incluido sin coste.' },
]

// Las 7 preguntas canónicas, en el orden canónico (MENSAJES-MAESTROS §10).
// VeriFactu es la última: cierra, no abre.
const faqItems = reactive([
  {
    question: '¿Qué pasa si mi caja sigue sin cuadrar después de instalarlo?',
    answer: 'Que por fin sabrás dónde mirar. ComandaPlus no hace desaparecer los descuadres por arte de magia: los saca a la luz. El cierre Z te enseña el descuadre tal cual es, con el desglose por camarero, por turno y por método de pago. Un bar mediano pierde hasta 3.000 € al año en descuadres invisibles (Ninja TPV). La palabra clave es "invisibles".',
    open: false,
  },
  {
    question: '¿Mi camarero senior va a aceptar cambiar de sistema?',
    answer: 'Es tan fácil como WhatsApp: tocas el plato, tocas la mesa, ya está. No hay menús anidados ni teclas de función. Y no tiene que cambiar su forma de trabajar: sigue tomando nota en la mesa, solo que en el móvil en vez de en la libreta. El camarero nuevo sirve la primera tarde sin que estés encima.',
    open: false,
  },
  {
    question: '¿Y si una noche se cae internet?',
    answer: 'El camarero puede seguir tomando pedidos en su móvil: se guardan en el propio dispositivo y se envían a cocina en cuanto vuelve la conexión, sin que se pierda nada y sin que él tenga que hacer nada.',
    open: false,
  },
  {
    question: '¿Tengo que comprar tablet nueva?',
    answer: 'No. Funciona en el móvil que ya tienes, en una tablet vieja o en un portátil — cualquier cosa con navegador. El único "hardware" son los QR impresos de las mesas.',
    open: false,
  },
  {
    question: '¿Vais a subir el precio el año que viene?',
    answer: 'Tu precio queda congelado 3 años desde que entras. Si la cuota pública sube, tú sigues pagando lo mismo.',
    open: false,
  },
  {
    question: '¿Cómo me doy de baja si no me convence?',
    answer: 'Lo dejas y ya está. Sin permanencia, sin penalizaciones, sin llamadas para retenerte. Cancelas en 60 segundos desde tu panel. Los primeros 30 días, además, te devolvemos el dinero íntegro si no te convence.',
    open: false,
  },
  {
    question: '¿VeriFactu cubre también si soy autónomo?',
    answer: 'VeriFactu está en desarrollo y estará listo antes del plazo: enero de 2027 para sociedades y julio de 2027 para autónomos. Cuando se active, va incluido sin coste adicional. Solo el 8 % del sector estaba listo a noviembre de 2025 (Autónomos y Emprendedor), y adaptar un TPV viejo cuesta entre 1.500 € y 8.000 €.',
    open: false,
  },
])

// ─── SEO — VeriFactu y "TPV" fuera del título (MENSAJES-MAESTROS §3 y §11) ──
useHead({
  title: 'Tu caja cuadra el lunes | ComandaPlus',
  meta: [
    { name: 'description', content: 'Cierre Z que cuadra solo. Y si no cuadra, sabes con qué camarero y en qué turno. La mesa de 8 paga en 30 segundos. Listo en una tarde, sin hardware.' },
    { property: 'og:title', content: 'Tu caja cuadra el lunes. Y la mesa de 8 paga en 30 segundos.' },
    { property: 'og:description', content: 'El sistema con el que tomas comandas, las mandas a cocina y cobras — desde el móvil que ya tienes. Sin hardware. Sin permanencia. Listo en una tarde.' },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: 'https://comandaplus.com' },
    { property: 'og:locale', content: 'es_ES' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Tu caja cuadra el lunes. Y la mesa de 8 paga en 30 segundos.' },
    { name: 'twitter:description', content: 'Sin hardware. Sin permanencia. Listo en una tarde.' },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      }),
    },
  ],
})

onMounted(() => {
  const sinMovimiento = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  // Animaciones de scroll: se respetan las preferencias del sistema.
  if (sinMovimiento) {
    document.querySelectorAll('.scroll-fade').forEach((el) => el.classList.add('visible'))
  } else {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    )
    document.querySelectorAll('.scroll-fade').forEach((el) => observer.observe(el))
  }

  // Vídeos: no hay autoplay en móvil ni con reduced-motion.
  // En escritorio arrancan solos, pero SIEMPRE con botón de pausa visible.
  const esMovil = window.matchMedia('(max-width: 900px)').matches
  if (!sinMovimiento && !esMovil) {
    const arranca = (el: HTMLVideoElement | null, flag: Ref<boolean>) => {
      if (!el) return
      el.preload = 'metadata'
      el.play().then(() => { flag.value = true }).catch(() => { flag.value = false })
    }
    arranca(heroVideo.value, heroPlaying)
    arranca(analyticsVideo.value, analyticsPlaying)
  }

  // Scroll suave. Guard contra href="#" (lanzaba una excepción de JS).
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', (e) => {
      const href = (anchor as HTMLAnchorElement).getAttribute('href')
      if (!href || href === '#') return
      const target = document.querySelector(href)
      if (target) {
        e.preventDefault()
        menuAbierto.value = false
        target.scrollIntoView({ behavior: sinMovimiento ? 'auto' : 'smooth', block: 'start' })
      }
    })
  })
})
</script>

<style scoped>
/* ─── BASE ─── */
.landing {
  --header-h: 80px;
  font-family: 'Geist', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background-color: #F7F6F2;
  color: #0B0B0A;
  line-height: 1.7;
  font-size: 16px;
}

/* Las anclas aterrizan en el título, no debajo de la cabecera. */
section[id] { scroll-margin-top: calc(var(--header-h) + 16px); }

/* Skip link */
.skip-link {
  position: absolute;
  left: -9999px;
  top: 0;
  z-index: 200;
  background: #0B0B0A;
  color: #F7F6F2;
  padding: 0.75rem 1.25rem;
  border-radius: 0 0 6px 0;
  font-weight: 600;
  text-decoration: none;
}
.skip-link:focus { left: 0; }

/* Foco visible propio, también sobre fondos negros. */
.landing a:focus-visible,
.landing button:focus-visible,
.landing input:focus-visible {
  outline: 3px solid #E0603C;
  outline-offset: 2px;
  border-radius: 4px;
}

/* ─── HEADER — sticky (como /para/[vertical]): NO tapa el titular ─── */
.landing-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: #0B0B0A;
  padding: 0.75rem 1rem;
}
.header-content {
  max-width: 1040px;
  margin: 0 auto;
  padding: 0.35rem 0.35rem 0.35rem 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}
.logo {
  font-size: 1.4rem;
  font-weight: 800;
  color: #F7F6F2;
  letter-spacing: -0.5px;
  text-decoration: none;
  white-space: nowrap;
}
.logo span { color: #E0603C; }

.landing-nav { display: flex; gap: 1.5rem; align-items: center; }
.landing-nav a {
  color: #C9C5BC;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  transition: color 0.2s;
}
.landing-nav a:hover { color: #E0603C; }

.landing-header .btn-primary {
  background: #F7F6F2;
  color: #0B0B0A;
  border-color: #F7F6F2;
  border-radius: 999px;
  padding: 0.6rem 1.25rem;
}
.landing-header .btn-primary:hover { background: #E4E2DC; border-color: #E4E2DC; }

/* Hamburguesa: 44×44 (mínimo táctil). Solo móvil/tablet. */
.nav-toggle {
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(247, 246, 242, 0.25);
  border-radius: 10px;
  background: transparent;
  color: #F7F6F2;
  cursor: pointer;
  flex-shrink: 0;
}
.nav-toggle .material-symbols-rounded { font-size: 26px; }

.nav-movil { display: none; }

/* ─── BOTONES ─── */
.btn-primary {
  background: #0B0B0A;
  color: #F7F6F2;
  padding: 0.85rem 1.5rem;
  border: 1px solid #0B0B0A;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: background 120ms ease, transform 80ms ease;
  text-decoration: none;
  display: inline-block;
}
.btn-primary:hover { background: #2A2A28; transform: translateY(-1px); }
.btn-primary:active { transform: translateY(0); }
.btn-secondary {
  background: transparent;
  color: #0B0B0A;
  padding: 0.85rem 1.5rem;
  border: 1px solid #0B0B0A;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 120ms ease;
  text-decoration: none;
  display: inline-block;
}
.btn-secondary:hover { background: #E4E2DC; }
/* Variante para fondo oscuro (hero) */
.btn-secondary--dark {
  color: #F7F6F2;
  border-color: rgba(247, 246, 242, 0.45);
}
.btn-secondary--dark:hover { background: rgba(247, 246, 242, 0.12); }

/* ─── HERO ─── */
.hero-band { background-color: #0B0B0A; }
.hero {
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
}
.hero-text h1 {
  font-size: clamp(2rem, 4.6vw, 3.1rem);
  font-weight: 800;
  line-height: 1.15;
  color: #F7F6F2;
  margin: 0 0 1.25rem;
  letter-spacing: -1px;
}
/* La frase que faltaba: QUÉ ES el producto (test de los 5 segundos). */
.hero-what {
  font-size: 1.2rem;
  font-weight: 600;
  color: #F7F6F2;
  line-height: 1.5;
  margin: 0 0 1rem;
  max-width: 520px;
}
.hero-text .subheadline {
  font-size: 1.05rem;
  color: #C9C5BC;
  margin-bottom: 1.75rem;
  max-width: 500px;
  line-height: 1.65;
}
.hero-buttons { display: flex; gap: 1rem; flex-wrap: wrap; }
.hero .btn-primary {
  background: #F7F6F2;
  color: #0B0B0A;
  border-color: #F7F6F2;
}
.hero .btn-primary:hover { background: #E4E2DC; border-color: #E4E2DC; }

/* WhatsApp: secundario y pequeño, nunca el botón principal. */
.hero-whatsapp {
  margin: 1.25rem 0 0;
  font-size: 0.9rem;
  color: #C9C5BC;
}
.hero-whatsapp a {
  color: #F7F6F2;
  text-decoration: underline;
  text-underline-offset: 3px;
  font-weight: 600;
}

.hero-trust {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 1.5rem;
  font-size: 0.9rem;
  color: #C9C5BC;
}
.hero-trust span { display: inline-flex; gap: 0.4rem; }
.hero-trust strong { color: #F7F6F2; font-weight: 700; }

.hero-qr { display: flex; flex-direction: column; align-items: center; gap: 1rem; }
figure { margin: 0; }
.hero-product-mockup { width: 100%; }
.hero-product-mockup .hero-video {
  display: block;
  width: 100%;
  max-width: 320px;
  height: auto;
  aspect-ratio: 352 / 720;
  margin: 0 auto;
  background: transparent;
  filter: drop-shadow(0 12px 28px rgba(11, 11, 10, 0.18));
}

/* Control de pausa de los vídeos (WCAG 2.2.2, Nivel A) */
.video-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0.75rem auto 0;
  padding: 0.55rem 1rem;
  min-height: 44px;
  background: transparent;
  border: 1px solid rgba(247, 246, 242, 0.4);
  border-radius: 999px;
  color: #F7F6F2;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}
.video-toggle:hover { background: rgba(247, 246, 242, 0.12); }
.video-toggle .material-symbols-rounded { font-size: 20px; }
.hero-product-mockup { display: flex; flex-direction: column; align-items: center; }
.video-toggle--light {
  border-color: #C9C5BC;
  color: #3F3B37;
}
.video-toggle--light:hover { background: #E4E2DC; }

/* ─── SECCIONES "ASÍ SE USA" ─── */
.usage { max-width: 1200px; margin: 0 auto; padding: 4rem 2rem; }
.usage--compact { padding-top: 2rem; padding-bottom: 3rem; }
.usage--dark { max-width: none; background: #0B0B0A; padding: 4rem 0; }
.usage--dark .usage-inner { max-width: 1200px; margin: 0 auto; padding: 0 2rem; }
.usage-head { max-width: 760px; margin: 0 auto 2.5rem; text-align: center; }
.usage-pain {
  font-size: 1.05rem;
  color: #7A4E11;
  font-style: italic;
  line-height: 1.5;
  margin: 0.75rem 0 1rem;
}
.usage-title {
  font-size: clamp(1.5rem, 3.4vw, 2.3rem);
  font-weight: 800;
  line-height: 1.2;
  color: #0B0B0A;
  letter-spacing: -0.5px;
  margin: 0;
}
.usage--dark .usage-title { color: #F7F6F2; }
.usage--dark .usage-pain { color: #C9C5BC; }
.usage--dark .section-label { color: #E0603C; }
.usage-note {
  font-size: 1rem;
  color: #4A4440;
  line-height: 1.6;
  margin: 1rem auto 0;
  max-width: 640px;
}

/* Citas verbatim (banco autorizado — MENSAJES-MAESTROS §12) */
.quote {
  max-width: 620px;
  margin: 1.75rem auto 0;
  padding: 1rem 1.25rem;
  border-left: 3px solid #A8391F;
  background: #FFFFFF;
  border-radius: 0 6px 6px 0;
  text-align: left;
}
.quote blockquote {
  margin: 0 0 0.5rem;
  font-size: 1rem;
  font-style: italic;
  color: #3F1A0F;
  line-height: 1.55;
}
.quote figcaption { font-size: 0.85rem; color: #6B6862; }
.usage--dark .quote {
  background: #1A1A18;
  border-left-color: #E0603C;
}
.usage--dark .quote blockquote { color: #F7F6F2; }
.usage--dark .quote figcaption { color: #C9C5BC; }

.usage-steps { list-style: none; padding: 0; margin: 0; display: grid; gap: 1.5rem; }
.usage-steps--mobile { grid-template-columns: repeat(4, 1fr); }
.usage-steps--three { grid-template-columns: repeat(3, 1fr); }
.usage-step { display: flex; flex-direction: column; gap: 1rem; }
.usage-step-text { display: flex; flex-direction: column; gap: 0.25rem; }
.usage-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #0B0B0A;
  color: #F7F6F2;
  font-weight: 700;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}
.usage--dark .usage-step-num { background: #A8391F; }
.usage-step-text h3 { font-size: 1.05rem; font-weight: 700; color: #0B0B0A; margin: 0; }
.usage--dark .usage-step-text h3 { color: #F7F6F2; }
.usage-step-text p { font-size: 0.92rem; color: #4A4440; line-height: 1.5; margin: 0; }
.usage--dark .usage-step-text p { color: #C9C5BC; }
.usage-cta { text-align: center; margin-top: 2.5rem; }

.usage-shot-img {
  width: 100%;
  height: auto;
  display: block;
  filter: drop-shadow(0 10px 22px rgba(11, 11, 10, 0.12));
}

.usage-stack {
  list-style: none;
  padding: 0;
  margin: 0 auto;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  gap: 3rem;
}
.usage-stack-head { text-align: center; align-items: center; margin-bottom: 1.25rem; }
.usage-shot-wide {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.45);
}
.usage-video { max-width: 1000px; margin: 0 auto; display: flex; flex-direction: column; align-items: center; }
.usage-video .usage-shot-wide {
  aspect-ratio: 16 / 9;
  box-shadow: 0 14px 36px rgba(11, 11, 10, 0.16);
  border: 1px solid #E4E2DC;
}

/* ─── LO MONTAS TÚ ─── */
.setup { max-width: 1080px; margin: 0 auto; padding: 4rem 2rem; text-align: center; }
.setup-title {
  font-size: clamp(1.5rem, 3.4vw, 2.3rem);
  font-weight: 800;
  color: #0B0B0A;
  letter-spacing: -0.5px;
  margin: 0.75rem auto 2.5rem;
  max-width: 700px;
}
.steps { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: repeat(3, 1fr); gap: 2.5rem; }
.step { text-align: center; }
.step-number {
  width: 56px;
  height: 56px;
  background: #A8391F;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  font-weight: 800;
  color: #fff;
  margin: 0 auto 1.25rem;
}
.step h3 { font-size: 1.15rem; font-weight: 700; margin-bottom: 0.5rem; color: #0B0B0A; }
.step p { font-size: 0.95rem; color: #4A4440; line-height: 1.6; }
.setup-shots { display: grid; grid-template-columns: 1fr; gap: 2rem; margin: 2.5rem auto 0; max-width: 1000px; }
.setup-shots img {
  width: 100%;
  height: auto;
  border-radius: 10px;
  border: 1px solid #E4E2DC;
  box-shadow: 0 8px 24px rgba(11, 11, 10, 0.10);
}
.setup-shots figcaption { margin-top: 0.75rem; font-size: 0.9rem; color: #4A4440; }

/* ─── TÍTULOS DE SECCIÓN ─── */
/* #A8391F sobre fondo claro = 5,94:1 (antes #D14A2C daba 4,11:1 y suspendía). */
.section-label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #A8391F;
  text-align: center;
  margin-bottom: 1rem;
}
.section-title {
  font-size: clamp(1.7rem, 4.6vw, 2.6rem);
  font-weight: 800;
  text-align: center;
  margin-bottom: 1rem;
  color: #0B0B0A;
  letter-spacing: -0.5px;
}
.section-subtitle {
  text-align: center;
  color: #4A4440;
  font-size: 1.05rem;
  max-width: 620px;
  margin: 0 auto 3rem;
}

/* ─── CALCULADORA ─── */
.calc {
  background: #fff;
  border-top: 1px solid #E4E2DC;
  border-bottom: 1px solid #E4E2DC;
  padding: 4rem 2rem;
}
.calc-inner { max-width: 1100px; margin: 0 auto; text-align: center; }
.calc-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-top: 2rem;
  text-align: left;
}
.calc-inputs { display: flex; flex-direction: column; gap: 1.25rem; }
.calc-input { display: flex; flex-direction: column; gap: 0.4rem; }
.calc-label {
  font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace;
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #6B6862;
  font-weight: 500;
}
.calc-input input {
  font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace;
  font-size: 1.5rem;
  font-weight: 700;
  color: #0B0B0A;
  background: #F7F6F2;
  border: 1px solid #E4E2DC;
  border-radius: 4px;
  padding: 0.65rem 0.9rem;
  outline: none;
  transition: border-color 120ms ease;
  font-variant-numeric: tabular-nums;
}
.calc-input input:focus { border-color: #0B0B0A; }
.calc-hint { font-size: 0.85rem; color: #6B6862; }

.calc-output {
  background: #F7F6F2;
  border: 1px solid #E4E2DC;
  border-radius: 4px;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}
.calc-output-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  font-size: 0.95rem;
  color: #3F1A0F;
  padding: 0.4rem 0;
  border-bottom: 1px dashed #E4E2DC;
}
.calc-output-row:last-of-type { border-bottom: 0; }
.calc-output-total {
  border-top: 2px solid #0B0B0A !important;
  border-bottom: 0 !important;
  padding-top: 0.75rem !important;
  margin-top: 0.5rem;
}
.calc-output-total span { font-weight: 700; color: #0B0B0A; }
.calc-num {
  font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace;
  font-variant-numeric: tabular-nums;
  color: #0B0B0A;
  white-space: nowrap;
}
.calc-num-big { font-size: 1.3rem; color: #A8391F; }
.calc-output-cost {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.9rem;
  color: #6B6862;
  margin-top: 0.25rem;
}
.calc-output-ratio {
  background: #0B0B0A;
  color: #F7F6F2;
  padding: 0.85rem 1rem;
  border-radius: 4px;
  text-align: center;
  font-size: 0.95rem;
  margin-top: 0.5rem;
}
.calc-output-ratio strong { color: #fff; font-size: 1.05rem; }
.calc-cta { margin-top: 0.5rem; text-align: center; }
.calc-note {
  font-size: 0.85rem;
  color: #6B6862;
  text-align: center;
  margin: 0.5rem 0 0 0;
  line-height: 1.4;
}

/* ─── LO QUE NO TE PEDIMOS ─── */
.why-us { max-width: 1200px; margin: 0 auto; padding: 4rem 2rem; }
.why-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem; }
.why-card {
  background: #fff;
  padding: 1.75rem;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border-left: 4px solid #A8391F;
}
.why-icon {
  font-size: 26px;
  color: #A8391F;
  margin-bottom: 0.5rem;
  display: block;
}
.why-card h3 { font-size: 1.1rem; font-weight: 700; margin-bottom: 0.5rem; color: #0B0B0A; }
.why-card p { font-size: 0.95rem; color: #4A4440; line-height: 1.6; margin: 0; }

/* ─── PRECIOS ─── */
.pricing { max-width: 1200px; margin: 0 auto; padding: 4rem 2rem; }
.pricing-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  align-items: stretch;
}
.plan {
  background: #fff;
  border: 1px solid #E4E2DC;
  border-radius: 16px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}
.plan.featured { border: 2px solid #A8391F; }
.plan-badge {
  position: absolute;
  top: -13px;
  left: 50%;
  transform: translateX(-50%);
  background: #A8391F;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 0.35rem 1.25rem;
  border-radius: 20px;
  white-space: nowrap;
}
.plan-name { font-size: 1.15rem; font-weight: 700; color: #0B0B0A; margin-bottom: 0.35rem; }
.plan-limit { font-size: 0.9rem; color: #6B6862; margin-bottom: 1.25rem; min-height: 2.6em; }
.plan-price { font-size: 2.8rem; font-weight: 800; color: #0B0B0A; line-height: 1; margin-bottom: 0.25rem; }
.plan-price span { font-size: 1rem; font-weight: 500; color: #6B6862; }
.plan-period { font-size: 0.9rem; color: #6B6862; margin-bottom: 1.5rem; min-height: 2.6em; }
.plan-features { list-style: none; flex-grow: 1; margin: 0 0 1.5rem; padding: 0; }
.plan-features li {
  font-size: 0.9rem;
  color: #4A4440;
  padding: 0.5rem 0 0.5rem 1.75rem;
  position: relative;
  border-bottom: 1px solid #F5F0EB;
}
.plan-features li:last-child { border-bottom: none; }
.plan-features li::before {
  content: "\2713";
  position: absolute;
  left: 0;
  color: #A8391F;
  font-weight: 700;
}
.plan .btn-primary { width: 100%; text-align: center; }

.verifactu-notice {
  max-width: 720px;
  margin: 2.5rem auto 0;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: #FEF7E0;
  border: 1px solid rgba(168, 57, 31, 0.25);
  border-radius: 10px;
  font-size: 0.9rem;
  color: #4A4440;
  line-height: 1.6;
}
.verifactu-icon { font-size: 22px; color: #A8391F; flex-shrink: 0; }
.verifactu-notice strong { color: #0B0B0A; }

/* ─── FAQ ─── */
.faq { max-width: 800px; margin: 0 auto; padding: 4rem 2rem; }
.faq-list { margin-top: 1.5rem; }
.faq-item { border-bottom: 1px solid #E8E5DF; }
.faq-question {
  width: 100%;
  background: none;
  border: none;
  padding: 1.15rem 0;
  min-height: 44px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-family: inherit;
  font-size: 1.05rem;
  font-weight: 600;
  color: #0B0B0A;
  text-align: left;
  transition: color 0.2s;
}
.faq-question:hover { color: #A8391F; }
.faq-toggle {
  font-size: 1.5rem;
  font-weight: 300;
  color: #A8391F;
  transition: transform 0.3s;
  flex-shrink: 0;
  margin-left: 1rem;
}
.faq-item.open .faq-toggle { transform: rotate(45deg); }
.faq-answer {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s ease, padding 0.4s ease;
}
/* 300px cortaba las respuestas largas en móvil (bug real, auditoría UX). */
.faq-item.open .faq-answer {
  max-height: 1200px;
  padding-bottom: 1.25rem;
}
.faq-answer p {
  font-size: 0.95rem;
  color: #4A4440;
  line-height: 1.7;
  max-width: 62ch;
  margin: 0;
}

/* ─── CTA FINAL ─── */
.cta-final {
  max-width: 1000px;
  margin: 2rem auto 4rem;
  padding: 4rem 3rem;
  text-align: center;
  background: linear-gradient(135deg, #0B0B0A 0%, #2D2824 100%);
  border-radius: 24px;
}
.cta-final h2 {
  font-size: clamp(1.7rem, 4.6vw, 2.4rem);
  font-weight: 800;
  color: #F7F6F2;
  margin-bottom: 1rem;
  letter-spacing: -0.5px;
}
.cta-final p {
  font-size: 1.05rem;
  color: #C9C5BC;
  margin: 0 auto 2rem;
  max-width: 540px;
  line-height: 1.7;
}
.cta-final .btn-primary {
  background: #F7F6F2;
  color: #0B0B0A;
  border-color: #F7F6F2;
  font-size: 1.05rem;
  padding: 1rem 2.5rem;
}
.cta-final .btn-primary:hover { background: #E4E2DC; border-color: #E4E2DC; }

/* ─── FOOTER ─── */
.landing-footer {
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem 5rem;
  border-top: 1px solid #E4E2DC;
}
.footer-cols {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-bottom: 2rem;
}
.footer-col { display: flex; flex-direction: column; }
.footer-title {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #6B6862;
  margin-bottom: 0.5rem;
}
.footer-col a {
  color: #4A4440;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  padding: 0.6rem 0; /* 44px táctil */
  min-height: 44px;
  display: flex;
  align-items: center;
}
.footer-col a:hover { color: #A8391F; text-decoration: underline; }
.footer-copy {
  font-size: 0.9rem;
  color: #6B6862;
  margin: 0;
  padding-top: 1.5rem;
  border-top: 1px solid #E4E2DC;
}

/* ─── BARRA INFERIOR MÓVIL (zona del pulgar) ─── */
.cta-bar { display: none; }

/* ─── RESPONSIVE ─── */
@media (max-width: 900px) {
  /* El menú NO se borra: se pliega en hamburguesa. */
  .landing-nav { display: none; }
  .header-cta { display: none; }
  .nav-toggle { display: inline-flex; }

  .nav-movil {
    display: flex;
    flex-direction: column;
    max-width: 1040px;
    margin: 0.5rem auto 0;
    padding: 0.5rem 0 0.75rem;
    border-top: 1px solid rgba(247, 246, 242, 0.15);
  }
  .nav-movil a {
    color: #F7F6F2;
    text-decoration: none;
    font-size: 1rem;
    font-weight: 600;
    padding: 0.85rem 0.5rem;
    min-height: 44px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid rgba(247, 246, 242, 0.08);
  }
  .nav-movil-cta {
    justify-content: center;
    margin-top: 0.85rem;
    background: #F7F6F2 !important;
    color: #0B0B0A !important;
    border-color: #F7F6F2 !important;
    border-radius: 999px;
    border-bottom: 0 !important;
  }

  .hero { grid-template-columns: 1fr; text-align: center; padding: 2.5rem 1.5rem; gap: 2rem; }
  .hero-what, .hero-text .subheadline { margin-left: auto; margin-right: auto; }
  .hero-buttons { justify-content: center; }
  .hero-trust { justify-content: center; gap: 1rem; font-size: 0.85rem; }

  .steps { grid-template-columns: 1fr; gap: 2rem; }
  .pricing-grid { grid-template-columns: 1fr; max-width: 440px; margin: 0 auto; }
  .why-grid { grid-template-columns: 1fr; }
  .calc-grid { grid-template-columns: 1fr; gap: 2rem; }
  .usage-steps--mobile, .usage-steps--three { grid-template-columns: 1fr 1fr; }

  /* La barra inferior fija: el CTA nunca a más de un pulgar. */
  .cta-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 90;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.65rem 1rem calc(0.65rem + env(safe-area-inset-bottom));
    background: rgba(11, 11, 10, 0.96);
    backdrop-filter: blur(10px);
    border-top: 1px solid rgba(247, 246, 242, 0.12);
  }
  .cta-bar-link {
    color: #F7F6F2;
    text-decoration: none;
    font-weight: 600;
    font-size: 0.95rem;
    padding: 0.6rem 0.5rem;
    min-height: 44px;
    display: flex;
    align-items: center;
  }
  .cta-bar-btn {
    background: #F7F6F2;
    color: #0B0B0A;
    border-color: #F7F6F2;
    border-radius: 999px;
    min-height: 44px;
  }
  /* Que la barra no tape el pie */
  .landing-footer { padding-bottom: 6rem; }
}

@media (max-width: 560px) {
  .usage-steps--mobile, .usage-steps--three { grid-template-columns: 1fr; }

  /* Los mockups verticales (640×1309) ocupaban casi una pantalla cada uno:
     4 pasos = 4 pantallas de scroll. Acotados, la sección cabe en una. */
  .usage-step {
    flex-direction: row;
    align-items: center;
    gap: 1rem;
    text-align: left;
  }
  .usage-shot-img { max-width: 116px; flex-shrink: 0; }
  .usage-step-text { text-align: left; align-items: flex-start; }

  .usage { padding: 2.5rem 1.25rem; }
  .usage--dark { padding: 2.5rem 0; }
  .usage--dark .usage-inner { padding: 0 1.25rem; }
  .usage-stack { gap: 2rem; }
  .setup, .why-us, .pricing, .faq { padding: 2.5rem 1.25rem; }
  .calc { padding: 2.5rem 1.25rem; }
  .cta-final { margin: 1.5rem 1rem 3rem; padding: 2.5rem 1.5rem; }
  .footer-cols { grid-template-columns: 1fr 1fr; }
  .hero-text h1 { font-size: 1.85rem; }

  /* Compactación móvil: menos pantallas de scroll sin perder argumentario. */
  .section-subtitle { margin-bottom: 2rem; }
  .usage-head { margin-bottom: 2rem; }
  .setup-title { margin-bottom: 2rem; }
  .setup-shots { gap: 1.5rem; margin-top: 2rem; }

  .plan { padding: 1.5rem 1.25rem; }
  .plan-limit, .plan-period { min-height: 0; margin-bottom: 0.75rem; }
  .plan-features { margin-bottom: 1.25rem; }
  .plan-features li { font-size: 0.88rem; padding: 0.4rem 0 0.4rem 1.6rem; }
  .pricing-grid { gap: 1.25rem; }

  .why-card { padding: 1.25rem; }
  .why-grid { gap: 1rem; }
  .why-icon { margin-bottom: 0.25rem; }

  .calc-inputs { gap: 0.9rem; }
  .calc-input input { font-size: 1.2rem; padding: 0.5rem 0.75rem; }
  .calc-output { padding: 1.25rem; }
}

/* ─── ANIMACIONES ─── */
.scroll-fade {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1), transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}
.scroll-fade.visible { opacity: 1; transform: translateY(0); }

@media (prefers-reduced-motion: reduce) {
  .landing * {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
  .scroll-fade { opacity: 1; transform: none; }
}
</style>
